import React from 'react';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {formatPrice} from 'core/helpers';
import cs from 'classnames';
import {OnlineDiscountBadge} from './OnlineDiscountBadge';
import {SpecialPriceBadge} from './SpecialPriceLabel';
import {OnlineOnlyBadge} from './OnlineOnlyBadge';
import {UnitPriceBadge} from './UnitPriceBadge';
import {OutOfStockBadge} from './OutOfStockBadge';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {CashbackLabel} from 'components/CashbackLabel';
import {RUB_SYMBOL} from 'core/constants';

type TPriceProps = IProductSnippet['price'];

const CURRENCY_MAP = {
    ['RUB']: RUB_SYMBOL,
};

interface IGetPriceCurrency {
    currency?: 'RUB' | null;
    value: number | null;
}

const getPriceWithCurrency = ({currency, value}: IGetPriceCurrency) => {
    const currencyValue = CURRENCY_MAP[currency as keyof typeof CURRENCY_MAP] || CURRENCY_MAP.RUB;

    return `${formatPrice(value || 0)} ${currencyValue}`;
};

export const Price = (props: TPriceProps) => {
    const {unit, currency, cashback, percent, old, current} = props;

    return (
        <div className={style.main}>
            <Price.Badges badges={props.badges} unit={unit} currency={currency} />
            <Price.Values cashback={cashback} currency={currency} current={current} percent={percent} old={old} />
        </div>
    );
};

Price.Base = (props: Pick<TPriceProps, 'currency' | 'current' | 'cashback'>) => {
    const {current, currency, cashback} = props;

    const currentPrice = getPriceWithCurrency({value: current, currency});

    return (
        <div className={cs(style.main, style.main__price)}>
            <div className={style.price} data-skeleton-item={''}>
                <Typography
                    variants={{DT: 'price-24-b', TB: 'price-20-b', MB: 'price-20-b'}}
                    className={cs(style.price__value)}
                >
                    {currentPrice}
                </Typography>
                {cashback && <CashbackLabel value={cashback} className={style.cashback} />}
            </div>
        </div>
    );
};

Price.Values = (props: Pick<TPriceProps, 'old' | 'current' | 'currency' | 'percent' | 'cashback'>) => {
    const {current, old, currency, percent, cashback} = props;

    const oldPrice = getPriceWithCurrency({value: old, currency});
    const currentPrice = getPriceWithCurrency({value: current, currency});
    const isCurrentPriceOnly = current && !old;

    return (
        <div className={cs(style.main, style.main__price)}>
            <div className={style.discount} data-skeleton-item={''}>
                {Boolean(old) && (
                    <Typography className={style.discount__value} variant={'label-12-r'}>
                        {oldPrice}
                    </Typography>
                )}
                {Boolean(percent) && (
                    <Typography className={style.discount__percent} variant={'label-12-r'}>
                        -{percent}%
                    </Typography>
                )}
                {Boolean(cashback) && <CashbackLabel value={cashback} className={style.cashback} />}
            </div>
            <div className={style.price} data-skeleton-item={''}>
                <Typography
                    variants={{DT: 'price-24-b', TB: 'price-20-b', MB: 'price-20-b'}}
                    className={cs(style.price__value, {[style.price__value_sale]: Boolean(old)})}
                >
                    {currentPrice}
                </Typography>
                {Boolean(cashback) && isCurrentPriceOnly && (
                    <CashbackLabel value={cashback} className={style.cashback} />
                )}
            </div>
        </div>
    );
};

// eslint-disable-next-line complexity
Price.Badges = (props: Pick<TPriceProps, 'badges' | 'unit' | 'currency'>) => {
    const {
        unit,
        currency,
        badges: {hasUnitPrice, isSpecialPrice, isOnlineDiscount, isOnlineOnly, isOutOfStock},
    } = props;

    const unitPrice = getPriceWithCurrency({value: unit, currency});

    if (!hasUnitPrice && !isOnlineOnly && !isOnlineDiscount && !isSpecialPrice && !isOutOfStock) {
        return null;
    }

    return (
        <div className={style.labels} data-skeleton-item={''}>
            {hasUnitPrice && <UnitPriceBadge className={style.badge} unitPrice={unitPrice} />}
            {isSpecialPrice && <SpecialPriceBadge className={style.badge} />}
            {isOnlineDiscount && <OnlineDiscountBadge className={style.badge} isAnimated={false} />}
            {isOnlineOnly && <OnlineOnlyBadge className={style.badge} />}
            {isOutOfStock && <OutOfStockBadge className={style.badge} />}
        </div>
    );
};
